import React, { useState, useEffect } from "react";
import {
  RowsContainer,
  Row,
  InputContainer,
  IconContainer
} from "./index.styles";
import { RotaButton, RotaButtonIcon, RotaInput } from "@teamrota/rota-design";
import { useContacts } from "./hooks/useContacts";
import useBulkMutateAccountContacts from "./hooks/useUpdateContacts";

const ContactsPanel = ({ accountId }) => {
  const { contacts } = useContacts(accountId);

  const { bulkMutateAccountContacts } = useBulkMutateAccountContacts();

  useEffect(() => {
    setUpdatedContacts(
      contacts.map(contact => ({ ...contact, isNew: false, isDeleted: false }))
    );
  }, [contacts]);

  const [updatedContacts, setUpdatedContacts] = useState([]);

  const [validationErrors, setValidationErrors] = useState({});

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateContacts = contacts => {
    const errors = {};
    let hasErrors = false;

    contacts.forEach((contact, index) => {
      errors[index] = {};

      if (!contact.name || contact.name.length < 5) {
        errors[index].name = "Name must be at least 5 characters";
        hasErrors = true;
      }

      if (!contact.email || !validateEmail(contact.email)) {
        errors[index].email = "Please enter a valid email address";
        hasErrors = true;
      }
    });

    return { errors, hasErrors };
  };

  const handleAddContact = () => {
    setUpdatedContacts([
      ...updatedContacts,
      {
        // Temporary id, will be ignored on the backend due to the isNew flag
        id: Math.random()
          .toString(36)
          .substring(2, 15),
        name: "",
        email: "",
        isNew: true,
        isDeleted: false
      }
    ]);
  };

  const handleContactChange = (index, field, value) => {
    const replacementContacts = [...updatedContacts];
    replacementContacts[index][field] = value;
    setUpdatedContacts(replacementContacts);

    const { errors } = validateContacts(replacementContacts);
    setValidationErrors(errors);
  };

  const handleDeleteContact = index => {
    const replacementContacts = [...updatedContacts];
    replacementContacts[index].isDeleted = true;
    setUpdatedContacts(replacementContacts);
  };

  const handleSave = async () => {
    const { errors, hasErrors } = validateContacts(updatedContacts);
    setValidationErrors(errors);

    if (!hasErrors) {
      try {
        await bulkMutateAccountContacts({
          accountId,
          contacts: updatedContacts.map(
            ({ id, name, email, isNew, isDeleted }) => ({
              id,
              name,
              email,
              isNew,
              isDeleted
            })
          )
        });
      } catch (error) {
        console.error("Failed to update contacts:", error);
      }
    }
  };

  // Check if any contacts have been added, deleted or changed
  const isUpdatedContactsDifferent = () => {
    if (contacts.length !== updatedContacts.length) return true;

    if (updatedContacts.filter(contact => contact.isNew).length >= 1)
      return true;
    if (updatedContacts.filter(contact => contact.isDeleted).length >= 1)
      return true;

    return updatedContacts.some((updatedContact, index) => {
      const originalContact = contacts[index];
      const isNameDifferent = updatedContact.name !== originalContact?.name;
      const isEmailDifferent = updatedContact.email !== originalContact?.email;
      return isNameDifferent || isEmailDifferent;
    });
  };

  const isNoErrors = Object.values(validationErrors).every(
    errors => !errors.name && !errors.email
  );
  const isSaveEnabled = isUpdatedContactsDifferent() && isNoErrors;

  return (
    <div>
      <RowsContainer>
        {updatedContacts
          ?.filter(contact => !contact.isDeleted)
          .map((contact, index) => (
            <Row key={index}>
              <InputContainer>
                <RotaInput
                  label="Contact name"
                  value={contact.name}
                  onChange={event => {
                    handleContactChange(index, "name", event.target.value);
                  }}
                  isError={!!validationErrors[index]?.name}
                  errorMessage={validationErrors[index]?.name}
                />
              </InputContainer>

              <InputContainer>
                <RotaInput
                  label="Contact email"
                  value={contact.email}
                  onChange={event => {
                    handleContactChange(index, "email", event.target.value);
                  }}
                  isError={!!validationErrors[index]?.email}
                  errorMessage={validationErrors[index]?.email}
                />
              </InputContainer>

              <IconContainer>
                <RotaButtonIcon
                  icon="delete"
                  onClick={() => {
                    handleDeleteContact(index);
                  }}
                  disabled={contacts?.length === 1}
                />
              </IconContainer>
            </Row>
          ))}
        <div style={{ display: "flex", gap: "8px" }}>
          <RotaButton
            disabled={!isSaveEnabled}
            variant={"primary"}
            onClick={handleSave}
          >
            Save
          </RotaButton>

          <RotaButton
            disabled={false}
            variant={"outlined"}
            onClick={handleAddContact}
          >
            Add another
          </RotaButton>
        </div>
      </RowsContainer>
    </div>
  );
};

export default ContactsPanel;
